import React from 'react'
import _ from 'underscore'
import styled from 'styled-components'

import ZoneStatus from 'pages/shared/DeviceTabs/ZoneStatus'
import ServiceContractIcon from 'pages/shared/DeviceTabs/ServiceContractIcon'

import Tooltip from 'components/Tooltip'

import { useStoreContext } from 'hooks/StoreContext'
import useNexiaStream from 'hooks/useNexiaStream'

import { updateAlertsSeverity, highestSeverityForIcon, highestSeverityByAlertCount } from 'helpers/alerts'
import { color } from 'styles/colors'
import { screenSize } from 'styles/shared'

import { longDate, shortDate } from 'helpers/formatters'

import { DeviceBase } from 'types/Device'

type Props = {
  customerName?:string
  device: DeviceBase,
  isActive: boolean
}

const DeviceTabHeader = ({ customerName, device, isActive }: Props) => {
  const { reportCache } = useStoreContext()
  const { eventData: report } = useNexiaStream(device.deviceId, 'alarms', reportCache, device.optedIn)

  const severity = report ? highestSeverityForIcon(updateAlertsSeverity(report.alerts || [])) : highestSeverityByAlertCount(device)

  const ServiceContractInfo = () => {
    let contractInfo = <></>

    if (device.serviceContractStatus === 'Active' && device.serviceContractStartAt && device.serviceContractEndAt) {
      contractInfo = <span> {shortDate(device.serviceContractStartAt)} - {shortDate(device.serviceContractEndAt)} </span>
    } else if (device.serviceContractStatus === 'Active' && !device.serviceContractStartAt && device.serviceContractEndAt) {
      contractInfo = <span> Expires on {longDate(device.serviceContractEndAt)} </span>
    } else if (device.serviceContractStatus === 'Expired') {
      contractInfo = <span> Expired on {longDate(device.serviceContractEndAt)} </span>
    }

    return (
      <>
        <S.ServiceContractInfo>
          <span><b>{device.serviceContractStatus !== null && device.serviceContractStatus !== 'None' ? `Service Contract ${device.serviceContractStatus}` : 'No Service Contract'}</b></span>
        </S.ServiceContractInfo>
        {contractInfo}
      </>
    )
  }

  return (
    <S.Container>
      <S.AlertColor isActiveTab={isActive} severity={severity} aria-label={severity ? `highest alert severity: ${severity}` : 'no alerts'} />

      <S.Content>
        <S.Left>
          <S.Row>
            <Tooltip
              title={
                <S.TooltipContent>
                  <S.Zone>
                    <S.Label>
                      <ZoneStatus device={device} />
                    </S.Label>
                  </S.Zone>
                  <table>
                    <tbody>
                      <tr>
                        <td> <S.Label>Model:</S.Label> </td>
                        <td> <span>{device.deviceModel}</span> </td>
                      </tr>
                      <tr>
                        <td> <S.Label>AUID:</S.Label> </td>
                        <td> <span>{device.deviceId}</span> </td>
                      </tr>
                      <tr>
                        <td> <S.Label>S/N:</S.Label> </td>
                        <td> <span>{_.isEmpty(device.serialNumber) ? '--' : device.serialNumber}</span> </td>
                      </tr>
                    </tbody>
                  </table>
                  <ServiceContractInfo />
                </S.TooltipContent>
              }
              showArrow={false}
              placement="right"
            >
              <S.Name isActiveTab={isActive}>{device.name || device.deviceId}</S.Name>
            </Tooltip>
          </S.Row>
          <S.Row>
            <S.ModelNumber isActiveTab={isActive}>{device.deviceModel}</S.ModelNumber>

            <S.AUID isActiveTab={isActive}>{device.deviceId}</S.AUID>
          </S.Row>
        </S.Left>
        <S.Right>
          <ServiceContractIcon displayIcon customerName={customerName} device={device} />
        </S.Right>
      </S.Content>
    </S.Container>
  )
}

export default DeviceTabHeader

const S = {
  Container: styled.div`
    width: 200px;

    @media ${screenSize.small} {
      width: 245px;
    }
  `,
  AlertColor: styled.div.withConfig({
    shouldForwardProp: (prop) => !['isActiveTab', 'severity'].includes(prop)
  })<{ severity: string | null, isActiveTab: boolean }>`
    height: 7px;
    width: 100%;
    background-color: ${({ severity, isActiveTab }) => {
    if (severity) {
      switch (severity) {
        case 'major-sda':
          return isActiveTab ? color('majorSdaAlert') : color('majorSdaAlertInactive')
        case 'minor-sda':
          return isActiveTab ? color('minorSdaAlert') : color('minorSdaAlertInactive')
        default:
          return isActiveTab ? color(`${severity}Alert`) : color(`${severity}AlertInactive`)
      }
    } else {
      return 'inherit'
    }
  }};
    border-radius: 2px;
  `,
  Content: styled.div`
    display: flex;
    gap: 2px;
    padding: 5px;
    font-size: .85rem;
    text-transform: uppercase;
    justify-content: space-between;
  `,
  Row: styled.div`
    display: flex;
  `,
  Left: styled.div`
    text-align: left;
    margin-left: 5px;
  `,
  Right: styled.div`
    display: flex;
    justify-content: flex-end;
    margin-right: 5px;
    align-items: center;
  `,
  TooltipContent: styled.div`
    padding: 10px;
  `,
  Label: styled.label`
    margin-right: 5px;
    color: ${color('deviceTooltipLabelText')};
  `,
  Zone: styled.div`
    padding: 0px 2px 5px;
  `,
  Name: styled.div.withConfig({
    shouldForwardProp: (prop) => !['isActiveTab'].includes(prop)
  })<{ isActiveTab: boolean }>`
    font-weight: bold;
    font-size: 1rem;
    text-transform: capitalize;
    width: 150px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    cursor: pointer;
    margin-bottom: 5px;
    color: ${({ isActiveTab }) => color(isActiveTab ? 'primaryText' : 'inactiveTabText')};
  `,
  ModelNumber: styled.span.withConfig({
    shouldForwardProp: (prop) => !['isActiveTab'].includes(prop)
  })<{ isActiveTab: boolean }>`
    color: ${({ isActiveTab }) => color(isActiveTab ? 'primaryText' : 'inactiveTabText')};
    min-height: 14.875px;
  `,
  AUID: styled.span.withConfig({
    shouldForwardProp: (prop) => !['isActiveTab'].includes(prop)
  })<{ isActiveTab: boolean }>`
    margin-left: 10px;
    min-height: 14.875px;
    font-weight: 300;
    color: ${({ isActiveTab }) => color(isActiveTab ? 'primaryText' : 'inactiveTabText')};
  `,
  ServiceContractInfo: styled.div`
    padding-top: 10px;
    color: ${color('deviceTooltipLabelText')};
  `
}
